export const projects = [
  {
    id: 1,
    title: 'Microplastics in Alpine Freshwater Ecosystems',
    slug: 'microplastics-alpine-freshwater',
    description:
      'Tracking microplastic accumulation across 12 glacial-fed rivers in the Swiss Alps over 18 months. Sampling twice weekly, publishing raw data after every field session.',
    fullDescription:
      'This study tracks microplastic particle accumulation and distribution across 12 glacial-fed rivers in the Swiss Alps at elevations between 1,400 and 2,900 metres. Sampling occurs twice weekly at fixed GPS coordinates, with water and sediment samples processed for particle size, morphology, and polymer type using FTIR spectroscopy. All raw data is published within 24 hours of each session. The driving question: are alpine freshwater systems acting as sinks or transit pathways for microplastic pollution, and does altitude or glacial meltwater fraction influence accumulation patterns?',
    category: 'Biology',
    tags: ['Ecology', 'Pollution', 'Field Study'],
    status: 'Live',
    author: {
      name: 'Elena Vasquez',
      initials: 'EV',
      university: 'University of Zurich',
      country: 'Switzerland',
      color: '#52D68A',
    },
    startDate: 'Sep 2023',
    lastUpdate: '2 hours ago',
    followers: 312,
    updates: 47,
    recentUpdate:
      'Day 47 pH readings show a consistent 0.3-unit drop across all sites — this wasn\'t in my original hypothesis. Going back to the literature tonight.',
    gradient: 'linear-gradient(135deg, #052e16 0%, #14532d 100%)',
    timeline: [
      {
        date: 'March 14, 2024',
        title: 'Unexpected pH anomaly across all 12 sites',
        content: 'Day 47 field session. pH readings dropped 0.3 units from baseline across every site — not just a few. My hypothesis only accounted for site-specific variance. Spent the evening going through the literature and found one paper from 2019 suggesting glacial meltwater carbonate dilution could explain this pattern at this elevation range. Pulling the carbonate hardness data from my water samples now.',
        type: 'finding',
      },
      {
        date: 'February 28, 2024',
        title: 'FTIR results from the last 4 weeks — polyethylene dominant',
        content: 'Processed 88 samples from February. Polymer composition: 62% polyethylene, 19% polypropylene, 11% polystyrene, 8% other. The PE dominance is consistent with previous weeks but significantly higher than lowland river studies in the same region. I suspect fibres from outdoor clothing and gear — this is a high-traffic hiking area. Next step: morphology analysis to see if the fragment shape supports the textile hypothesis.',
        type: 'data',
      },
      {
        date: 'January 19, 2024',
        title: 'Equipment calibration error — two weeks of data flagged',
        content: 'Found a calibration drift in the particle counter used from Jan 3–16. The drift is systematic (consistent offset), so I can correct it mathematically, but I\'m publishing the raw and corrected datasets separately so the error is fully visible. Recalibrated the instrument and ran parallel tests with a secondary counter for the next two sessions to verify. Transparency note: update #31 data is provisional pending correction.',
        type: 'correction',
      },
      {
        date: 'December 5, 2023',
        title: 'First 90 days: what the data looks like so far',
        content: 'Completed the first 90-day sampling block. Total: 216 sessions, 864 samples processed. Overall finding: particle concentrations increase with altitude, contradicting my original hypothesis that concentrations would mirror the catchment population density. The elevation gradient is significant (p<0.001). Working hypothesis: orographic precipitation effect concentrates atmospheric microplastic deposition at higher elevations. Will need at least another 6 months to test this properly.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Heinrich Mueller',
        initials: 'HM',
        affiliation: 'WSL Institute for Snow and Avalanche Research',
        color: '#38BDF8',
        date: 'March 15, 2024',
        text: 'The elevation-concentration gradient you\'re seeing matches unpublished preliminary data we collected in 2022. We didn\'t pursue it but your pH anomaly is interesting — have you considered that the carbonate buffering capacity differs significantly between your high and low altitude sites? That could explain both findings.',
      },
      {
        author: 'Sara Kimura',
        initials: 'SK',
        affiliation: 'EPFL Environmental Engineering',
        color: '#A78BFA',
        date: 'February 29, 2024',
        text: 'Your textile fibre hypothesis for the PE dominance is compelling. For morphology analysis: I\'ve found that polarised light microscopy at 40x works better than standard brightfield for distinguishing textiles from film fragments in alpine samples. Happy to share the protocol if useful.',
      },
      {
        author: 'Lucas Brandt',
        initials: 'LB',
        affiliation: 'Independent, Alpine Ecology',
        color: '#52D68A',
        date: 'January 22, 2024',
        text: 'Really appreciate you publishing the calibration correction transparently. I\'ve seen three papers in my field where similar drift errors were quietly corrected in revised data with no mention. The separate raw/corrected files approach should be the standard.',
      },
    ],
  },
  {
    id: 2,
    title: 'CRISPR-Cas9 Off-Target Effects in E. coli Resistance',
    slug: 'crispr-ecoli-resistance',
    description:
      'Investigating whether targeted CRISPR edits to antibiotic-resistance genes in E. coli produce systematic off-target effects in clinical isolates. All gel images and sequencing data posted openly.',
    fullDescription:
      'This project investigates whether CRISPR-Cas9 edits targeting antibiotic-resistance genes in clinical E. coli isolates produce consistent off-target effects elsewhere in the genome. The clinical isolates come from three partner hospitals and were selected specifically because they carry resistance to carbapenems — last-resort antibiotics. Every guide RNA sequence, editing protocol, gel image, and sequencing result is posted in real time. The goal is not just to characterise off-target effects, but to understand whether they are consistent enough across isolates to be predictable — and therefore designable around.',
    category: 'Microbiology',
    tags: ['CRISPR', 'Antibiotic Resistance', 'Genomics'],
    status: 'Live',
    author: {
      name: 'James Okafor',
      initials: 'JO',
      university: 'MIT',
      country: 'USA',
      color: '#38BDF8',
    },
    startDate: 'Jan 2024',
    lastUpdate: '18 min ago',
    followers: 541,
    updates: 29,
    recentUpdate:
      'Run #12 failed again — found off-target edits at chromosome 14 in the control group. Contamination or genuine signal? Posting full gel images now.',
    gradient: 'linear-gradient(135deg, #0c1a2e 0%, #0e3461 100%)',
    timeline: [
      {
        date: 'March 16, 2024',
        title: 'Run #12: off-target edits in control group — urgent question',
        content: 'This is a significant anomaly. Whole-genome sequencing from Run #12 shows off-target edits at a locus on chromosome 14 in the control group — the group that wasn\'t supposed to receive any guide RNA. Two possibilities: (1) contamination during library preparation, or (2) a genuine background editing rate from residual Cas9 protein from a previous experiment. Posting all gel images and raw sequencing files now. Need external eyes on this before I proceed.',
        type: 'finding',
      },
      {
        date: 'March 8, 2024',
        title: 'Run #11 results: consistent pattern emerging at gyrA off-target site',
        content: 'Run #11 confirms what I saw in runs 8 and 9: consistent off-target activity at the gyrA-adjacent locus. This isn\'t random. The edits appear in 7 of the 12 isolates tested, at frequencies between 0.3% and 2.1%. The guide RNA I\'m using has a 2-base mismatch with the gyrA region, which according to the Doench et al. model shouldn\'t be sufficient for off-target cleavage — but clearly it sometimes is in these clinical isolates. Genomic context may matter more than the models predict.',
        type: 'finding',
      },
      {
        date: 'February 21, 2024',
        title: 'Protocol update: switching to higher-fidelity Cas9 variant',
        content: 'Based on the off-target pattern from runs 7-9, I\'m switching from SpCas9 to eSpCas9 (enhanced specificity variant) for runs 10+. The goal is to determine whether the off-target effects I\'m seeing are a Cas9 specificity issue or something specific to the genomic context of these clinical isolates. Running both in parallel for the next three experiments as an internal comparison.',
        type: 'method',
      },
      {
        date: 'February 3, 2024',
        title: 'First results: more off-target activity than expected',
        content: 'Runs 1–6 complete. The on-target efficiency is excellent (94% average). But I\'m seeing off-target activity at 3 sites I didn\'t predict from the bioinformatics analysis. Two of the sites are in noncoding regions, but one is adjacent to a gene involved in membrane permeability. That\'s potentially significant for resistance — I didn\'t design this experiment to look at that gene, but the data is clear. Updating the project scope.',
        type: 'data',
      },
    ],
    comments: [
      {
        author: 'Prof. Feng Zhang\'s Group',
        initials: 'FZ',
        affiliation: 'Broad Institute',
        color: '#52D68A',
        date: 'March 17, 2024',
        text: 'The chromosome 14 control group finding is almost certainly contamination — we\'ve seen this pattern before with residual Cas9. Recommendation: run a PCR for the Cas9 sequence on your negative controls for the last 3 experiments. Also, your gyrA off-target data is interesting and would be worth a separate focused study.',
      },
      {
        author: 'Dr. Amina Diallo',
        initials: 'AD',
        affiliation: 'Institut Pasteur, Paris',
        color: '#F59E0B',
        date: 'March 10, 2024',
        text: 'The isolate-specific off-target variation you\'re documenting (7/12 showing the gyrA-adjacent edit) may correlate with methylation patterns at that locus. Clinical isolates often have distinct methylation profiles from lab strains. Worth checking your SMRT sequencing data if you have it.',
      },
      {
        author: 'Kevin Park',
        initials: 'KP',
        affiliation: 'Johns Hopkins Biomedical Engineering',
        color: '#A78BFA',
        date: 'February 23, 2024',
        text: 'Your eSpCas9 comparison is exactly the right experiment. One suggestion: also try running GUIDE-seq alongside your WGS for the parallel runs — it\'ll give you a more sensitive off-target detection method and lets you cross-validate. The sensitivity difference is about 10-fold for low-frequency edits.',
      },
    ],
  },
  {
    id: 3,
    title: 'Quantum Coherence in Photosynthetic Pigments at 293K',
    slug: 'quantum-coherence-photosynthesis',
    description:
      'Testing whether long-lived quantum coherence in light-harvesting complexes persists at room temperature using femtosecond laser spectroscopy. Setup took five months to calibrate.',
    fullDescription:
      'This project uses two-dimensional electronic spectroscopy (2DES) to probe quantum coherence lifetimes in the Fenna-Matthews-Olson (FMO) light-harvesting complex at room temperature (293K). The central debate in quantum biology is whether the coherence signatures observed at cryogenic temperatures survive thermal decoherence at physiological temperatures. Setup involved five months of laser system calibration, including a custom-built pulse shaper. All raw 2DES data matrices, pump-probe traces, and calibration records are published after every measurement session.',
    category: 'Physics',
    tags: ['Quantum Biology', 'Spectroscopy', 'Photosynthesis'],
    status: 'Paused',
    author: {
      name: 'Mei Lin',
      initials: 'ML',
      university: 'ETH Zurich',
      country: 'Switzerland',
      color: '#A78BFA',
    },
    startDate: 'Mar 2023',
    lastUpdate: '3 days ago',
    followers: 428,
    updates: 31,
    recentUpdate:
      'Pausing measurements for equipment recalibration after a power surge corrupted two weeks of data. Resuming in approximately 3 weeks.',
    gradient: 'linear-gradient(135deg, #1a0533 0%, #2d1b69 100%)',
    timeline: [
      {
        date: 'March 12, 2024',
        title: 'Pausing: power surge corrupted two weeks of data',
        content: 'A building power surge on March 10 caused a hardware fault in the delay stage controller, which introduced systematic timing errors into all measurements taken since February 24. I\'ve identified the affected data (sessions 28–31) and flagged them in the repository. The hardware is being repaired and the delay stage will be recalibrated from scratch before I resume. Estimated downtime: 3 weeks. All corrupted data files remain published with a contamination flag — they\'re not useful for analysis but the corruption pattern itself may be informative.',
        type: 'correction',
      },
      {
        date: 'February 19, 2024',
        title: 'Strongest coherence signal yet — 400 fs at 293K',
        content: 'Session 27 produced the most clearly resolved coherence signature in the project so far: a 400 femtosecond beating signal in the cross-peak at the B800-B850 energy gap, at room temperature. This is longer than the purely vibrational decoherence models predict. I\'m being careful not to over-interpret — three alternative explanations remain viable — but the signal is real and reproducible across three consecutive sessions. Posting the full 2DES maps now.',
        type: 'finding',
      },
      {
        date: 'January 8, 2024',
        title: 'Solved the laser drift problem: temperature stabilisation',
        content: 'The source of the measurement drift I\'ve been fighting since October was finally traced to thermal expansion in the non-collinear optical parametric amplifier (NOPA) pump path. The lab temperature fluctuates by ±1.5°C over 24 hours, which is enough to shift the pointing by ~10 µrad — small but significant for interferometric measurements. Installed thermal stabilisation around the critical path. First measurement session after the fix showed 4x improvement in reproducibility.',
        type: 'method',
      },
      {
        date: 'October 15, 2023',
        title: 'Six months in: what I\'ve learned and what still doesn\'t work',
        content: 'Six-month summary. The FMO preparation is clean (confirmed by absorption spectroscopy). The 2DES setup is functioning. But reproducibility remains a problem — the coherence signals I see in one session don\'t always appear in the next, even with identical conditions. I\'ve ruled out sample degradation. I suspect it\'s a laser stability issue I haven\'t fully characterised yet. Publishing a full uncertainty analysis of all 18 sessions to date.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Gregory Engel',
        initials: 'GE',
        affiliation: 'University of Chicago',
        color: '#52D68A',
        date: 'February 20, 2024',
        text: 'The 400 fs signal at the B800-B850 cross-peak is consistent with what we\'ve seen in similar experiments. Your concern about the vibrational versus electronic coherence interpretation is exactly right — I\'d strongly recommend running the isotope substitution control (deuterated solvent) to help distinguish them. The frequency shift should be diagnostic.',
      },
      {
        author: 'Kai-Wei Chen',
        initials: 'KC',
        affiliation: 'National Taiwan University, Laser Optics',
        color: '#F59E0B',
        date: 'January 12, 2024',
        text: 'The NOPA thermal expansion diagnosis matches a problem I had in 2022. One addition to your stabilisation: if the pump beam path goes through a cage system, the cage itself can flex with temperature. I wrapped mine with foam insulation and got another 2x reproducibility improvement. Might be worth trying.',
      },
      {
        author: 'Yolanda Nkosi',
        initials: 'YN',
        affiliation: 'Max Planck Institute for Chemical Physics',
        color: '#A78BFA',
        date: 'March 14, 2024',
        text: 'Really sorry to hear about the power surge — that\'s genuinely devastating. One suggestion for your recalibration: consider implementing a real-time interferometric lock on the delay stage rather than relying on the motorised controller. It\'s more work to set up but makes you immune to this type of hardware fault.',
      },
    ],
  },
  {
    id: 4,
    title: 'Deep Learning for Early-Stage Pancreatic Cancer Detection',
    slug: 'ml-pancreatic-cancer-detection',
    description:
      'Training transformer-based models on anonymized CT scan datasets to flag early-stage pancreatic ductal adenocarcinoma. Model checkpoints and training logs published after each run.',
    fullDescription:
      'Pancreatic ductal adenocarcinoma (PDAC) has a 5-year survival rate of ~11% when detected at late stage, compared to ~44% at stage I. The problem is almost entirely one of detection: the pancreas is anatomically difficult to image, early tumours are small and isodense with surrounding tissue, and radiologists typically review each scan in under 90 seconds. This project trains transformer-based segmentation and classification models on a dataset of 4,200 anonymised CT scans (2,100 confirmed PDAC, 2,100 age-matched controls) from two academic medical centres. Model checkpoints, training logs, confusion matrices, and all evaluation metrics are published after every training run.',
    category: 'Computational Biology',
    tags: ['Machine Learning', 'Oncology', 'Medical Imaging'],
    status: 'Live',
    author: {
      name: 'Arjun Sharma',
      initials: 'AS',
      university: 'Stanford University',
      country: 'USA',
      color: '#F59E0B',
    },
    startDate: 'Nov 2023',
    lastUpdate: '1 hour ago',
    followers: 697,
    updates: 38,
    recentUpdate:
      'Model v0.4 hits 87.3% sensitivity on validation — still too many false positives in the 40-50 age cohort. Adjusting class weights and retraining tonight.',
    gradient: 'linear-gradient(135deg, #1c0f00 0%, #451a03 100%)',
    timeline: [
      {
        date: 'March 15, 2024',
        title: 'v0.4: 87.3% sensitivity, false positive problem persists in 40–50 cohort',
        content: 'Model v0.4 trained on the full 4,200-scan dataset with augmented preprocessing. Validation sensitivity: 87.3% (up from 82.1% in v0.3). Specificity: 79.4%. The good news is we\'ve crossed the 85% sensitivity threshold that my clinical collaborators consider minimum useful. The bad news: false positive rate in the 40–50 age cohort is 28%, compared to 14% in other age groups. This cohort has a higher prevalence of benign pancreatic cysts, and the model seems to be picking up cyst morphology as suspicious. Adjusting class weights and adding cyst-specific augmentation tonight.',
        type: 'data',
      },
      {
        date: 'February 27, 2024',
        title: 'Clinical review of v0.3 detections: model finds cases radiologists missed',
        content: 'Ran a retrospective clinical review with Dr. Chen (radiology, Stanford) on 50 cases the v0.3 model flagged as positive. Of the 14 true positives, 3 had been read as negative by the radiologist on original review. All three were Stage I tumours under 1.5 cm. This is exactly the use case. We\'re also seeing the model flag a consistent visual pattern in these cases — a subtle density gradient at the pancreatic duct that doesn\'t meet the standard radiology threshold but clearly correlates with early disease. Working on a saliency map visualisation to make this interpretable.',
        type: 'finding',
      },
      {
        date: 'February 5, 2024',
        title: 'v0.2 failure analysis: the model learned scanner type, not pathology',
        content: 'v0.2 achieved 91% accuracy on the validation set and I was briefly excited. Then I ran the stratified analysis. The model achieves 96% accuracy on scans from Hospital A and 71% on scans from Hospital B. That\'s a scanner/protocol confound, not a clinical finding. The two hospitals use different CT protocols and reconstruction kernels. I\'m now normalising all scans to a standardised HU window and adding scanner-type as a training variable. This is a painful lesson in the difference between validation accuracy and generalisability.',
        type: 'correction',
      },
      {
        date: 'December 20, 2023',
        title: 'Dataset v2: deidentification complete, training begins',
        content: 'After 6 weeks of IRB review, data use agreement negotiation, and deidentification audit, the full 4,200-scan dataset is clean and training-ready. Running baseline experiments with a standard U-Net segmentation architecture this week as a reference before moving to the transformer-based approach. Baseline sensitivity on held-out test set: 71.4%. This is the floor I need to beat. Publishing all dataset statistics (demographics, scanner types, tumour stage distribution) in the project notes.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Linda Chen',
        initials: 'LC',
        affiliation: 'Stanford Radiology',
        color: '#52D68A',
        date: 'March 16, 2024',
        text: 'The three Stage I cases your model found that I missed — I went back and looked at them with the model\'s attention maps as a guide. The duct density gradient it\'s picking up is real and subtle. I\'d never developed a specific search pattern for it because it wasn\'t in my training. This is a genuinely novel finding from a machine learning perspective and I think it\'s worth publishing as a standalone imaging feature paper, independent of the model.',
      },
      {
        author: 'Prof. Dina Katabi',
        initials: 'DK',
        affiliation: 'MIT CSAIL',
        color: '#38BDF8',
        date: 'February 8, 2024',
        text: 'The scanner confound problem in v0.2 is extremely common and almost universally underreported in published medical AI papers — I appreciate you documenting it so explicitly. For your normalisation: beyond HU windowing, consider histogram normalisation and N4 bias field correction before the model sees any scan. These have been most reliable in my experience for multi-site CT generalisation.',
      },
      {
        author: 'Marcus Williams',
        initials: 'MW',
        affiliation: 'Harvard Medical School, Pancreatic Cancer Research',
        color: '#F59E0B',
        date: 'March 1, 2024',
        text: 'Following this closely. The 40–50 false positive issue you\'re seeing is a known clinical challenge — that age group has the highest prevalence of incidental pancreatic cysts and branch-duct IPMNs. One suggestion: if you can obtain a subset of confirmed cyst cases from your hospital partners, adding them as a third class (rather than treating all non-PDAC as "normal") might significantly reduce this FP rate.',
      },
    ],
  },
  {
    id: 5,
    title: 'Biodegradable Polymers from Chlorella vulgaris Algal Biomass',
    slug: 'algal-biodegradable-plastics',
    description:
      'Synthesizing and characterizing polyhydroxyalkanoate (PHA) polymers grown from freshwater microalgae as a commercially viable alternative to petroleum-based plastics.',
    fullDescription:
      'This completed project developed a method for producing polyhydroxyalkanoate (PHA) biopolymers from Chlorella vulgaris microalgae cultivated in nitrogen-limited growth conditions. PHAs are naturally occurring bacterial storage polymers with mechanical properties comparable to conventional plastics, but they are fully biodegradable under soil and marine conditions. The challenge was adapting the bacterial PHA production pathway to microalgae, which offer advantages in production cost and CO₂ utilisation. After three years and 94 public updates, the project achieved PHA yields of 38% dry cell weight and tensile strength of 42 MPa — comparable to commercial PLA bioplastic. Results submitted to Green Chemistry journal.',
    category: 'Chemistry',
    tags: ['Green Chemistry', 'Polymers', 'Sustainability'],
    status: 'Completed',
    author: {
      name: 'Sofia Reinholt',
      initials: 'SR',
      university: 'Uppsala University',
      country: 'Sweden',
      color: '#52D68A',
    },
    startDate: 'Feb 2022',
    lastUpdate: '2 weeks ago',
    followers: 889,
    updates: 94,
    recentUpdate:
      'Final tensile strength at 42 MPa — comparable to commercial PLA. Submitted to Green Chemistry journal. Three years, 94 updates, and countless failed batches. Worth it.',
    gradient: 'linear-gradient(135deg, #052e16 0%, #1a3a1a 100%)',
    timeline: [
      {
        date: 'February 28, 2024',
        title: 'Paper submitted. Project complete.',
        content: 'Submitted to Green Chemistry. Final results: PHA yield 38% DCW (dry cell weight), up from 8% DCW in the first batches 24 months ago. Tensile strength 42 MPa, elongation at break 6.4%, Young\'s modulus 2.8 GPa. These are competitive with commercial PLA. The key breakthrough (update #67) was discovering that a brief phosphorus starvation period before nitrogen limitation significantly improves PHA accumulation — this wasn\'t in the literature and is the main novel finding of the paper. All raw data, NMR spectra, GPC traces, and mechanical testing data are in the public repository. The 94 updates are staying up permanently.',
        type: 'milestone',
      },
      {
        date: 'October 3, 2023',
        title: 'Update #67: phosphorus starvation breakthrough',
        content: 'This is the moment I\'ve been working toward for 20 months. Adding a 48-hour phosphorus starvation step before nitrogen limitation triples the PHA yield compared to nitrogen limitation alone. We\'re now consistently hitting 35–40% DCW. I don\'t fully understand the mechanism yet — my hypothesis is that phosphorus starvation activates a carbon overflow response that\'s separate from the nitrogen-response pathway. Whatever it is, it works reproducibly across six consecutive batches. This is the finding that makes this project commercially relevant.',
        type: 'finding',
      },
      {
        date: 'May 14, 2023',
        title: 'Update #31: almost quit — and why I didn\'t',
        content: 'I want to be honest about where I am. Fifteen months in, my best PHA yield is 11% DCW. The commercial threshold is 40%. I have data I don\'t understand, a hypothesis that\'s clearly incomplete, and a supervisor who is gently suggesting I consider pivoting the project. I wrote a long document last week about why I should quit. Then I re-read update #1 — I said this would take at least three years. I\'m going to honour that commitment. Posting the full methodological failure log for months 12–15. Someone somewhere will find something useful in it.',
        type: 'finding',
      },
      {
        date: 'February 10, 2022',
        title: 'Project starts: the question and the plan',
        content: 'Starting a three-year project on PHA biopolymers from Chlorella vulgaris. The question: can we achieve PHA yields and mechanical properties competitive with petroleum-based plastics using only a microalgal production system? My first-year target is 20% DCW yield. I have no idea if this is achievable. I\'m documenting everything publicly because I think the honest record of a long-term project — including all the failures — is as valuable as the final result.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Arun Krishnamurthy',
        initials: 'AK',
        affiliation: 'Indian Institute of Science',
        color: '#38BDF8',
        date: 'March 2, 2024',
        text: 'Congratulations on completing this. The phosphorus starvation finding in update #67 is genuinely novel — I\'ve reviewed the PHA literature thoroughly and this specific sequence (P-starvation before N-limitation) doesn\'t appear anywhere. It should be the lead finding of the paper, not a supplementary observation. I hope the reviewers recognise its significance.',
      },
      {
        author: 'Astrid Bergqvist',
        initials: 'AB',
        affiliation: 'Chalmers University, Biopolymers Group',
        color: '#F59E0B',
        date: 'October 5, 2023',
        text: 'I followed this project from update #8 and I want to say that update #31 (where you almost quit) is the most honest piece of science communication I\'ve ever read. I showed it to my entire lab. The willingness to document the crisis rather than hide it is exactly what Labora should be. Thank you.',
      },
      {
        author: 'Oliver Nakamura',
        initials: 'ON',
        affiliation: 'NovaBio Materials, R&D',
        color: '#52D68A',
        date: 'March 5, 2024',
        text: 'We\'ve been watching this project for a year. We manufacture PLA-based packaging and have been looking for a seaweed-derived replacement. Your yield and tensile data are exactly in the commercially viable range. Would you be open to a conversation about licensing or a collaborative scale-up study after publication?',
      },
    ],
  },
  {
    id: 6,
    title: 'Neuroplasticity Recovery After Adolescent Traumatic Brain Injury',
    slug: 'neuroplasticity-tbi-adolescents',
    description:
      'Longitudinal fMRI study tracking structural and functional neuroplasticity in adolescents (14–19) at 3, 6, and 12 months post-TBI, compared against matched healthy controls.',
    fullDescription:
      'A three-year longitudinal fMRI study of neuroplasticity patterns in 60 adolescents aged 14–19 with documented traumatic brain injury, compared against 60 age and sex-matched healthy controls. Imaging was conducted at 3, 6, and 12 months post-injury. The primary outcome measures were structural connectivity (DTI tractography), functional connectivity (resting-state fMRI), and cognitive performance across four domains. The central finding: a subset of adolescents show a distinctive plasticity pattern — initial functional decline followed by reorganisation via novel connectivity pathways — that is absent in adult TBI studies and may explain the age-dependent recovery advantage seen in adolescents. Published in Nature Neuroscience. All anonymised imaging data and analysis pipelines on OSF.',
    category: 'Neuroscience',
    tags: ['fMRI', 'TBI', 'Adolescent Health'],
    status: 'Completed',
    author: {
      name: 'David Nwankwo',
      initials: 'DN',
      university: 'University College London',
      country: 'UK',
      color: '#38BDF8',
    },
    startDate: 'Sep 2021',
    lastUpdate: '1 month ago',
    followers: 1043,
    updates: 112,
    recentUpdate:
      'Paper accepted in Nature Neuroscience. All anonymised imaging data and analysis pipelines are now on OSF. This one took 3 years — thank you to everyone who followed along.',
    gradient: 'linear-gradient(135deg, #0c1a2e 0%, #1e3a5f 100%)',
    timeline: [
      {
        date: 'February 14, 2024',
        title: 'Accepted: Nature Neuroscience. Data and code on OSF.',
        content: 'After two rounds of revision and a supplementary experiment requested by Reviewer 2, the paper is accepted. The core finding holds: approximately 35% of adolescent TBI participants showed a distinctive plasticity trajectory — significant functional connectivity decline at 3 months followed by re-routing via frontal-parietal circuits by 12 months — that closely correlates with the recovery advantage adolescents demonstrate over adults in cognitive outcome. This pattern doesn\'t appear in the adult TBI literature, which we believe explains an important piece of the age-recovery relationship. All data and analysis pipelines are on OSF under the link in the project notes.',
        type: 'milestone',
      },
      {
        date: 'August 22, 2023',
        title: '12-month imaging complete: the full picture is clearer than expected',
        content: 'Final imaging session complete. 58 of 60 participants completed all three timepoints (two withdrew for non-health reasons). The longitudinal picture is more coherent than I expected: the subgroup showing the novel plasticity pattern (n=21) has converged significantly toward control-group connectivity at 12 months. More striking is the cognitive outcome data — this subgroup outperforms both the remaining TBI participants AND the controls on two of four cognitive metrics by 12 months. Plasticity as enhancement, not just recovery.',
        type: 'finding',
      },
      {
        date: 'January 15, 2023',
        title: 'Unexpected finding at 6-month scan: a subgroup is showing something different',
        content: 'Six-month imaging results are in. For most participants, the pattern is as the literature predicts: gradual functional connectivity recovery toward baseline. But a subgroup of 19 participants (32%) is showing something I\'ve never seen described: connectivity that is NOT recovering toward baseline but instead reorganising via entirely different pathways — specifically a novel frontal-parietal route that bypasses the damaged region. I don\'t know what this means clinically yet. But it\'s consistent across the subgroup, and their cognitive scores at 6 months are not significantly worse than controls. Consulting with two neurologists before drawing any conclusions.',
        type: 'finding',
      },
      {
        date: 'December 8, 2021',
        title: 'Baseline imaging and recruitment complete',
        content: 'Baseline imaging complete for all 60 TBI participants and 60 controls. Recruitment took 3 months longer than planned — TBI cases with complete documentation and within the 3-month injury window were harder to find than the power calculation assumed. The delays were worth it: the cohort is well-matched on age, sex, SES, and pre-injury cognitive estimates. First 3-month follow-up imaging starts in March. Publishing all baseline demographics and imaging QC data now.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Sarah Thompson',
        initials: 'ST',
        affiliation: 'UCSF Neurology',
        color: '#A78BFA',
        date: 'February 16, 2024',
        text: 'I\'ve been following this since the 6-month finding and the paper is everything I hoped it would be. The frontal-parietal re-routing pattern you identified in the subgroup maps onto something we\'ve been seeing clinically for years but couldn\'t point to mechanistically. Requesting the OSF dataset now to see if we can replicate in our adult TBI cohort (where I\'d predict absence of this pattern, which would be equally informative).',
      },
      {
        author: 'Maya Osei-Mensah',
        initials: 'MO',
        affiliation: 'Parent of TBI survivor, Ghana',
        color: '#52D68A',
        date: 'February 17, 2024',
        text: 'My son had a TBI at 16. I\'ve been following this project for almost two years and read every update. I\'m not a scientist. But reading your research gave me something to hold onto during the hardest months. Thank you for making this public. Thank you for explaining it in a way I could understand. And congratulations — this matters to more people than the academic community.',
      },
      {
        author: 'Prof. Alex Chen',
        initials: 'AC',
        affiliation: 'Toronto Western Hospital, Neurosurgery',
        color: '#F59E0B',
        date: 'January 17, 2023',
        text: 'The subgroup finding at 6 months is remarkable. One clinical context that may help: we see a very similar pattern in patients who undergo intensive cognitive rehabilitation starting within 4 weeks of injury. It\'s possible your subgroup is accidentally stratified by rehabilitation intensity — do you have that data? If the re-routing pattern correlates with early intensive rehab, the clinical implications are significant.',
      },
    ],
  },
  {
    id: 7,
    title: 'Perovskite-Silicon Tandem Solar Cell Efficiency Under Real-World Conditions',
    slug: 'perovskite-silicon-tandem',
    description:
      'Fabricating monolithic perovskite-silicon tandem solar cells and characterizing their performance degradation under humidity, UV exposure, and temperature cycling — conditions absent from most lab-only studies.',
    fullDescription:
      'Laboratory perovskite-silicon tandem solar cells routinely exceed 33% efficiency — but lab conditions (controlled atmosphere, no UV, stable temperature) are nothing like real outdoor operation. This project fabricates monolithic two-terminal tandem cells and subjects them to accelerated aging tests that simulate 10 years of real-world outdoor conditions: damp-heat cycling (85°C/85% RH per IEC 61215), UV pre-conditioning, and freeze-thaw cycles. Every fabrication batch, efficiency measurement, EQE spectrum, and degradation curve is published after each characterisation. The goal: understand what actually limits perovskite tandem lifetime in real deployment, rather than the idealized conditions of most lab reports.',
    category: 'Materials Science',
    tags: ['Solar Energy', 'Perovskites', 'Photovoltaics'],
    status: 'Live',
    author: {
      name: 'Yuki Tanaka',
      initials: 'YT',
      university: 'Kyoto University',
      country: 'Japan',
      color: '#F59E0B',
    },
    startDate: 'Apr 2023',
    lastUpdate: '5 hours ago',
    followers: 476,
    updates: 55,
    recentUpdate:
      'Efficiency dropped from 28.1% to 24.6% after 500 hours of damp-heat exposure. This is actually better than expected — documenting everything in the supplementary log.',
    gradient: 'linear-gradient(135deg, #1c1000 0%, #3d2500 100%)',
    timeline: [
      {
        date: 'March 14, 2024',
        title: '500h damp-heat: 24.6% efficiency — better than the literature predicted',
        content: 'Update #55. After 500 hours at 85°C/85% RH (the IEC 61215 standard for 10-year outdoor equivalent), efficiency has dropped from 28.1% to 24.6% — a 12.5% relative decline. The published literature for perovskite-only cells shows 30–50% decline under the same conditions, so tandem architecture appears to confer a stability advantage I didn\'t predict. The silicon bottom cell acts as a moisture barrier that slows halide migration in the perovskite top cell. EQE shows the silicon subcell is essentially unchanged; all degradation is in the perovskite. Publishing full I-V curves and EQE spectra now.',
        type: 'data',
      },
      {
        date: 'January 31, 2024',
        title: 'Fabrication batch #8: highest initial efficiency yet at 28.1%',
        content: 'Batch #8 devices certified at 28.1% PCE on the first day — my highest so far and above my original project target of 27%. The improvement came from switching to a Cs/FA/MA triple-cation perovskite absorber composition and adding a thin MoO₃ recombination junction layer. More importantly, the within-batch efficiency variance dropped from ±2.1% to ±0.7%, which suggests the fabrication process is finally becoming reproducible. Starting the damp-heat aging test on these devices tonight.',
        type: 'finding',
      },
      {
        date: 'October 12, 2023',
        title: 'UV degradation faster than expected — finding the mechanism',
        content: 'Update #31. After 300 hours of UV pre-conditioning, efficiency dropped 18% — faster than the damp-heat degradation and faster than I expected from the literature. XPS analysis points to degradation of the spiro-OMeTAD hole transport layer under UV exposure. This isn\'t the perovskite degrading — it\'s the device architecture failing. Switching to an inorganic CuSCN hole transport layer for batches 7+ to test whether this fixes the UV stability issue.',
        type: 'finding',
      },
      {
        date: 'May 3, 2023',
        title: 'First cells fabricated: 22.4% efficiency, many problems to solve',
        content: 'First working tandem devices off the line. Certified efficiency: 22.4% — well below commercial Si single-junction, let alone the 33%+ seen in top labs. But it\'s a start and the devices are real. Main problems identified: (1) shunting at the perovskite-silicon tunnel junction, (2) high series resistance from the transparent top contact, (3) significant batch-to-batch variation. Documenting every fabrication step and failure mode openly — there\'s a lot of literature on final efficiencies but very little on what goes wrong at this stage.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Ulrich Paetzold',
        initials: 'UP',
        affiliation: 'Karlsruhe Institute of Technology',
        color: '#38BDF8',
        date: 'March 15, 2024',
        text: 'The 12.5% relative efficiency loss after 500h damp-heat is genuinely competitive with what we see in optimised perovskite single-junction cells. The silicon moisture barrier hypothesis is plausible — we see something similar in our 4-terminal tandem devices. One suggestion: measure the ion migration activation energy before and after aging (DLTS or IS) to see if the tandem architecture actually slows halide migration kinetics or just limits water ingress.',
      },
      {
        author: 'Hana Novak',
        initials: 'HN',
        affiliation: 'Czech Technical University, Photovoltaics',
        color: '#52D68A',
        date: 'January 3, 2024',
        text: 'Following this from the beginning. The MoO₃ recombination junction is a good call — we had similar variance problems with organic recombination layers. One thing that helped us: plasma treatment of the silicon surface immediately before perovskite deposition. The native oxide plays a bigger role than most papers acknowledge.',
      },
      {
        author: 'Dr. Martin Green\'s Group',
        initials: 'MG',
        affiliation: 'UNSW Sydney, Photovoltaics Research Centre',
        color: '#F59E0B',
        date: 'March 16, 2024',
        text: 'Your transparency about the UV/hole transport layer issue in update #31 is unusual in this field — most labs would have quietly switched HTL without noting it as a failure mode. The CuSCN substitution is the right call. Are you planning to publish the UV degradation data for the spiro-OMeTAD devices separately? It\'s useful negative data the community needs.',
      },
    ],
  },
  {
    id: 8,
    title: 'Antibiotic Resistance Gene Transfer in Urban Hospital Wastewater',
    slug: 'hospital-wastewater-amr',
    description:
      'Metagenomic sequencing of wastewater collected from three major European hospitals to map horizontal gene transfer of AMR genes — before, during, and after standard treatment processes.',
    fullDescription:
      'Hospitals are major reservoirs of antibiotic-resistant bacteria, and their wastewater is an understudied vector for the spread of antimicrobial resistance (AMR) genes into the broader environment. This project collects wastewater samples from three major European hospitals at four points in the treatment process: raw influent, primary settling, secondary biological treatment, and treated effluent before environmental discharge. Shotgun metagenomic sequencing characterises the resistome at each stage — the complete set of AMR genes present. The driving question: does standard hospital wastewater treatment eliminate AMR genes, reduce them, or potentially concentrate certain resistant strains by killing susceptible competitors?',
    category: 'Microbiology',
    tags: ['AMR', 'Metagenomics', 'Public Health'],
    status: 'Live',
    author: {
      name: 'Lena Hoffmann',
      initials: 'LH',
      university: 'TU Munich',
      country: 'Germany',
      color: '#52D68A',
    },
    startDate: 'Jun 2023',
    lastUpdate: '3 hours ago',
    followers: 388,
    updates: 41,
    recentUpdate:
      'Sequencing returned 14 unique resistance gene variants from just two hospitals. This dataset is denser than I expected — analysis will take months.',
    gradient: 'linear-gradient(135deg, #052e16 0%, #0f3525 100%)',
    timeline: [
      {
        date: 'March 13, 2024',
        title: '14 unique AMR gene variants from first two hospitals — unexpected density',
        content: 'First sequencing batch results are back. From Hospital A and Hospital B (Hospital C data still processing), the resistome includes 14 unique AMR gene variants I hadn\'t seen reported in wastewater literature for these two hospitals\' region. Three of the variants are associated with genes conferring resistance to carbapenems, colistin, and fosfomycin — all last-line antibiotics. The density of the resistome is significantly higher than I projected from the published literature on non-hospital urban wastewater. It\'s going to take months to fully characterise this dataset. Publishing all raw sequencing reads and the initial annotation file now.',
        type: 'finding',
      },
      {
        date: 'February 8, 2024',
        title: 'Preliminary finding: treatment doesn\'t eliminate — it transforms',
        content: 'Based on the first 12 samples (3 per hospital × 4 treatment stages), a preliminary pattern is emerging: standard biological treatment doesn\'t eliminate AMR genes — it changes the composition of the resistome. Beta-lactamase genes are significantly reduced (good). But carbapenem-resistance genes are proportionally enriched in the treated effluent compared to the raw influent. Hypothesis: secondary biological treatment selectively kills beta-lactam-susceptible organisms while leaving carbapenem-resistant strains viable, concentrating them relative to the total microbial load.',
        type: 'finding',
      },
      {
        date: 'December 19, 2023',
        title: 'Hospital C ethics approval: all three sites now active',
        content: 'Hospital C ethics committee approved sample collection after a 3-month review. All three sites are now active. Hospital C is a specialist oncology centre with very high antibiotic use per patient day — I predict its resistome will be denser than Hospitals A and B. First samples collected and in transit to the sequencing facility. Also publishing the full sampling protocol, chain-of-custody documentation, and biosafety approval documentation for all three hospitals.',
        type: 'milestone',
      },
      {
        date: 'August 2, 2023',
        title: 'First samples collected: hospitals A and B',
        content: 'First sampling day at both hospitals. Sample collection followed WHO wastewater sampling guidelines modified for clinical settings. I collected 2L at each of the 4 treatment stages at each hospital — 16 samples total, plus 4 field blanks. Everything was processed and frozen within 4 hours. DNA extraction starts tomorrow. The hospital infection control teams were genuinely supportive once they understood the research question — they want this data as much as we do. Posting all field notes and sample metadata now.',
        type: 'milestone',
      },
    ],
    comments: [
      {
        author: 'Dr. Timothy Walsh',
        initials: 'TW',
        affiliation: 'University of Oxford, AMR Research',
        color: '#38BDF8',
        date: 'March 14, 2024',
        text: 'The carbapenem-resistance enrichment finding in the treated effluent is alarming and important. We\'ve seen a related pattern in river sediments downstream from hospitals in India — the biological treatment process appears to act as a selection pressure that enriches for the most resistant strains. Your European hospital data will be the first clean comparison using this sampling design. When you have Hospital C data, please do publish a rapid communication — this is too important to wait for the full paper.',
      },
      {
        author: 'Prof. Ana Freitas',
        initials: 'AF',
        affiliation: 'University of Porto, Environmental Microbiology',
        color: '#A78BFA',
        date: 'February 10, 2024',
        text: 'The carbapenem enrichment hypothesis is consistent with something we published in 2022 for municipal wastewater (but municipal WWTPs don\'t have remotely the same AMR baseline as hospitals). For your analysis: I\'d recommend stratifying by gene mobility (integron-associated vs. chromosomal) — the mobile elements are the ones that spread to the environment, and they may behave differently through treatment.',
      },
      {
        author: 'Dr. Niels van der Berg',
        initials: 'NB',
        affiliation: 'RIVM, Netherlands',
        color: '#52D68A',
        date: 'December 20, 2023',
        text: 'We\'ve been trying to get a similar study approved through three Dutch hospitals for 18 months and have been blocked at ethics at every stage. Your documentation of the approval process for all three sites is genuinely useful — can you share the specific framing you used for the ethics applications? What worked where the "environmental surveillance" framing didn\'t?',
      },
    ],
  },
];

export const categories = ['All', 'Biology', 'Microbiology', 'Physics', 'Computational Biology', 'Chemistry', 'Neuroscience', 'Materials Science'];

export const getStatusColor = (status) => {
  switch (status) {
    case 'Live':      return { bg: 'rgba(82,214,138,0.12)',  text: '#52D68A', dot: '#52D68A' };
    case 'Completed': return { bg: 'rgba(56,189,248,0.12)',  text: '#38BDF8', dot: '#38BDF8' };
    case 'Paused':    return { bg: 'rgba(245,158,11,0.12)',  text: '#F59E0B', dot: '#F59E0B' };
    default:          return { bg: 'rgba(148,163,184,0.12)', text: '#94A3B8', dot: '#94A3B8' };
  }
};
